import React, { useRef } from 'react';
import Modal from './Modal';
import Icon from '../../shared/Icon';
import { othersWhoAskedClause } from '../sharedQueries';

const queryName = (thread) => thread?.title || 'Untitled conversation';

// "A", "A and B", "A, B and C", each name emphasised.
function NameList({ names }) {
  return names.map((name, i) => (
    <React.Fragment key={name}>
      {i > 0 && (i === names.length - 1 ? ' and ' : ', ')}
      <strong className="iter1-modal-name">{name}</strong>
    </React.Fragment>
  ));
}

/**
 * Shared Queries handoff: Share is a confirmation, not a toggle. Focus starts on Cancel. A
 * failure keeps the modal open with the error and changes nothing.
 */
export function ShareModal({ thread, datasetNames, error, busy, loading, onConfirm, onClose }) {
  const cancelRef = useRef(null);
  return (
    <Modal
      title="Share with your organization?"
      onClose={onClose}
      initialFocusRef={cancelRef}
      actions={(
        <>
          <button ref={cancelRef} type="button" className="resui-btn resui-btn--secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="resui-btn resui-btn--primary" disabled={busy || loading} onClick={onConfirm}>Share query</button>
        </>
      )}
    >
      <p>
        Everyone in your organization will be able to open <strong className="iter1-modal-name">{queryName(thread)}</strong> and ask follow-up questions in it.
      </p>
      <ul className="iter1-modal-list">
        <li>Each answer uses the access of the person who asked. Anyone with less access sees the question, not the answer.</li>
        {datasetNames.length > 0 && (
          <li>
            It uses <NameList names={datasetNames} />. People without access to {datasetNames.length === 1 ? 'it' : 'them'} can’t open the query.
          </li>
        )}
        <li>Only you can rename it, change its datasets, stop sharing or delete it.</li>
      </ul>
      {error && (
        <p className="iter1-modal-error" role="alert">
          <Icon name="warning-circle" size={16} /> <span>{error}</span>
        </p>
      )}
    </Modal>
  );
}

/** Stop sharing says who loses access before anything changes. Focus starts on Cancel. */
export function StopSharingModal({ thread, others, busy, loading, onConfirm, onClose }) {
  const cancelRef = useRef(null);
  const clause = othersWhoAskedClause(others);
  return (
    <Modal
      title="Stop sharing this query?"
      onClose={onClose}
      initialFocusRef={cancelRef}
      actions={(
        <>
          <button ref={cancelRef} type="button" className="resui-btn resui-btn--secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="resui-btn resui-btn--destructive" disabled={busy || loading} onClick={onConfirm}>Stop sharing</button>
        </>
      )}
    >
      <p>
        No one else in your organization will be able to open <strong className="iter1-modal-name">{queryName(thread)}</strong>
        {clause && `, including ${clause}`}. You keep the whole conversation.
      </p>
    </Modal>
  );
}
